import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Box, Button, Flex, Grid, Relative, ShadowOverlay, Text } from 'pcln-design-system';
import InputModal from '@pcln/input-modal';
import { themeGet } from '@styled-system/theme-get';
import Calendar from '../Calendar';
import DateField from '../DateField';
import formatDates from '../utils/formatDates';
import getLengthOfStay from '../utils/getLengthOfStay';
const StyledCalendar = /*#__PURE__*/styled(Calendar).withConfig({
  displayName: "DatePicker__StyledCalendar",
  componentId: "sc-8yne6i-0"
})(["&&&{position:", ";", " z-index:", ";margin-top:", ";}"], props => props.position || 'absolute', ({
  rightAlignCalendar
}) => rightAlignCalendar && 'right: 0;', props => props.zIndex, themeGet('space.2'));
const StyledShadowOverlay = /*#__PURE__*/styled(ShadowOverlay).withConfig({
  displayName: "DatePicker__StyledShadowOverlay",
  componentId: "sc-8yne6i-1"
})(["&&&{", "}"], props => props.hide && 'background-color: transparent;');
const StyledHeader = /*#__PURE__*/styled(Flex).withConfig({
  displayName: "DatePicker__StyledHeader",
  componentId: "sc-8yne6i-2"
})(["&&&{align-items:center;flex-direction:column;justify-content:flex-start;height:fit-content;width:100%;padding-top:", ";}"], themeGet('space.2'));
const defaultInitialDates = {
  startDate: null,
  endDate: null
};
function DatePicker({
  borderColor,
  dateTextColor,
  iconColor,
  className,
  disabled,
  errorMessage,
  hasShadowEffect = true,
  height,
  hideSelectButton,
  initialDates = defaultInitialDates,
  isInitiallyOpen,
  isMobile,
  label = 'Check-in - Check-out',
  name,
  noDateLabel,
  preventLabelWrap,
  rightAlignCalendar,
  selectTwoDates = true,
  showLengthOfStayCopy,
  use2024DesignSeti,
  width,
  zIndex = 2,
  onBlur,
  onChange,
  onClose,
  onFocus,
  onOpen,
  onToggle,
  ...calendarProps
}) {
  const [isOpen, setOpen] = useState(isInitiallyOpen);
  const [dates, setDates] = useState(initialDates);
  useEffect(() => {
    setOpen(isInitiallyOpen);
  }, [isInitiallyOpen]);
  const hideTooltip = useMemo(() => calendarProps?.hideTooltip || isOpen, [calendarProps.hideTooltip, isOpen]);
  const dateString = useMemo(() => formatDates(selectTwoDates, dates), [selectTwoDates, dates]);
  useEffect(() => {
    setDates(initialDates);
  }, [initialDates]);
  const handleOpen = useCallback(() => {
    onToggle?.();
    setOpen(true);
    onFocus?.();
    onOpen?.();
  }, [onFocus]);
  const handleFocus = useCallback(() => {
    if (!disabled && !isMobile) {
      setOpen(true);
      onFocus?.();
      onOpen?.();
    }
  }, [disabled, isMobile, onFocus]);
  const handleClose = useCallback(() => {
    onToggle?.();
    setOpen(false);
    onBlur?.();
    onClose?.();
  }, [dates, onBlur, onChange, onToggle]);
  const handleChange = useCallback((evt, dates) => {
    setDates(dates);
    if (dates?.endDate !== null) {
      onToggle?.();
      handleClose();
      onChange?.(dates);
    }
  }, [dates, handleClose]);
  const lengthOfStayCopy = getLengthOfStay(dates?.startDate, dates?.endDate);
  return React.createElement(Relative, null, isOpen && !isMobile && React.createElement(StyledShadowOverlay, {
    hide: !hasShadowEffect,
    zIndex: zIndex,
    onClick: handleClose,
    "data-testid": 'date-picker-overlay'
  }), React.createElement(DateField, {
    className: className,
    disabled: disabled,
    dateTextColor: dateTextColor,
    error: errorMessage,
    height: height,
    hideTooltip: hideTooltip,
    borderColor: borderColor,
    iconColor: iconColor,
    isMobile: isMobile,
    label: label,
    name: name,
    noDateLabel: noDateLabel,
    preventLabelWrap: preventLabelWrap,
    value: showLengthOfStayCopy && lengthOfStayCopy ? `${dateString} ${lengthOfStayCopy}` : dateString,
    width: width,
    zIndex: isOpen ? zIndex : undefined,
    onClick: handleOpen,
    onFocus: handleFocus
  }), isOpen && !isMobile && React.createElement(StyledCalendar, {
    zIndex: zIndex + 1,
    rightAlignCalendar: rightAlignCalendar,
    selectTwoDates: selectTwoDates,
    startDate: dates?.startDate,
    endDate: dates?.endDate,
    use2024DesignSeti: use2024DesignSeti,
    onChange: handleChange,
    onDismiss: handleClose,
    ...calendarProps
  }), isMobile && React.createElement(InputModal, {
    footer: use2024DesignSeti && !hideSelectButton && React.createElement(Button, {
      size: 'large',
      variation: 'fill',
      onClick: handleClose
    }, "Select Dates ", getLengthOfStay(dates?.startDate, dates?.endDate)),
    id: 'date-picker-modal',
    isOpen: isOpen,
    input: use2024DesignSeti ? React.createElement(Grid, {
      alignItems: 'flex-start',
      templateColumns: '1fr auto'
    }, React.createElement(StyledHeader, {
      "aria-label": 'Choose Travel Dates'
    }, React.createElement(Text, {
      fontSize: '3',
      fontWeight: 'bold',
      color: 'primary.dark',
      mb: 3
    }, "Choose Dates")), React.createElement(Box, {
      width: '40px',
      height: '40px',
      ml: '12px'
    })) : React.createElement(DateField, {
      error: errorMessage,
      hideTooltip: true,
      isMobile: true,
      label: label,
      name: name,
      preventLabelWrap: preventLabelWrap,
      value: showLengthOfStayCopy && lengthOfStayCopy ? `${dateString} ${lengthOfStayCopy}` : dateString,
      width: 1
    }),
    inputProps: use2024DesignSeti ? {
      alignItems: 'flex-start',
      px: 2,
      py: 2
    } : {
      alignItems: 'center',
      px: '12px',
      py: '12px'
    },
    subheading: use2024DesignSeti && React.createElement(Flex, {
      justifyContent: 'center',
      width: '100%'
    }, React.createElement(Text, {
      fontSize: '2',
      fontWeight: 'medium',
      color: 'text',
      mb: 2
    }, dateString ? `${dateString} ${lengthOfStayCopy}` : label)),
    zIndex: zIndex,
    onClose: handleClose
  }, React.createElement(Calendar, {
    startDate: dates?.startDate,
    endDate: dates?.endDate,
    isMobile: true,
    selectTwoDates: selectTwoDates,
    use2024DesignSeti: use2024DesignSeti,
    onChange: handleChange,
    onDismiss: handleClose,
    ...calendarProps
  })));
}
export default memo(DatePicker);